import { z } from 'zod';
import { BillingSchedulePreviewSchema } from './billing-schedule';
import { BillingIntervalSchema } from '../types/subscriptions/enums';
import { MoneyCentsSchema, PositiveMoneyCentsSchema, } from '../types/sale/v4/execution';
import { CheckoutSourceKindEnum, PublicExperienceSchemaVersionV2Schema, SubmissionWorkflowStatusSchema, } from './enums';
import { ProviderExecutionScopeSchema } from './execution-scope';
import { ExperienceTitleSchema } from './experience';
import { LastInvoiceStatusSchema, LiveRecurringStatusSchema } from './billing';
import { BusinessIdSchema, CheckoutIdSchema, ExperienceIdSchema, IsoTimestampSchema, StripeCustomerIdSchema, StripeInvoiceIdSchema, StripePaymentIntentIdSchema, StripeSubscriptionIdSchema, SubmissionIdSchema, } from './primitives';
/**
 * Public Experiences — merchant customer directory (Part 2).
 *
 * Customers, payments, invoices and recurring agreements live in the business's
 * connected account and are READ LIVE from the provider under the business's
 * execution scope. Nothing here is a stored billing mirror: the only persisted
 * document is `PxCustomerDocumentSchema`, which records identifiers, scope and
 * provenance so a customer can be authorized and associated without a provider
 * round trip.
 */
// ---------------------------------------------------------------------------
// Persisted: private customer linkage (PLAN §6.9)
// ---------------------------------------------------------------------------
/**
 * `/publicExperiencePrivate/{businessId}/pxCustomers/{stripeCustomerId}` —
 * written once (idempotent `create`) when the connected-account Customer is
 * created for a checkout. Identifiers, scope and provenance ONLY: no name,
 * email, phone, status or amount — those are read live from the provider.
 * Server-only (default deny).
 */
export const PxCustomerDocumentSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionV2Schema,
    businessId: BusinessIdSchema,
    executionScope: ProviderExecutionScopeSchema,
    stripeCustomerId: StripeCustomerIdSchema,
    sourceKind: z.literal(CheckoutSourceKindEnum.PUBLIC_EXPERIENCE_FORM),
    firstExperienceId: ExperienceIdSchema,
    firstSubmissionId: SubmissionIdSchema,
    firstCheckoutId: CheckoutIdSchema,
    createdAt: IsoTimestampSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Cursor pagination (provider-backed lists only; Firestore pagination untouched)
// ---------------------------------------------------------------------------
export const PAYFORM_CURSOR_DEFAULT_LIMIT = 20;
export const PAYFORM_CURSOR_MAX_LIMIT = 50;
/**
 * Opaque, server-minted cursor (base64url). It binds the page to
 * `{ businessId, objectAccountId, livemode, filterHash }` and the server rejects
 * a cursor whose binding does not match its own context (400). Clients never
 * construct or decode it.
 */
export const OpaqueCursorSchema = z
    .string()
    .min(1)
    .max(2048)
    .regex(/^[A-Za-z0-9_-]+$/, 'cursor must be an opaque base64url token');
/** `?cursor&limit` — `z.coerce` so query strings work. */
export const CursorQuerySchema = z.object({
    cursor: OpaqueCursorSchema.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(PAYFORM_CURSOR_MAX_LIMIT)
        .default(PAYFORM_CURSOR_DEFAULT_LIMIT),
});
const cursorPageShape = (itemSchema) => ({
    data: z.array(itemSchema),
    /** Present exactly when `hasMore` is true. */
    cursor: OpaqueCursorSchema.nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().min(1).max(PAYFORM_CURSOR_MAX_LIMIT),
});
const refineCursorPage = (page, ctx) => {
    if (page.hasMore !== (page.cursor !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cursor'],
            message: 'cursor must be present exactly when hasMore is true',
        });
    }
};
/**
 * `{ data, cursor, hasMore, limit }` — the cursor page envelope for
 * provider-backed lists. Deliberately carries NO totals (`total`, `totalPages`,
 * `page`, counts): the provider does not know them and the server never
 * fabricates one.
 */
export const createCursorPageSchema = (itemSchema) => z.object(cursorPageShape(itemSchema)).strict().superRefine(refineCursorPage);
/** The four cursor-page keys, for consumers that need to assert the shape. */
export const CURSOR_PAGE_KEYS = ['data', 'cursor', 'hasMore', 'limit'];
// ---------------------------------------------------------------------------
// Customer DTOs (live reads)
// ---------------------------------------------------------------------------
/**
 * Provider-side profile fields as stored on the Customer. They are not
 * re-validated as PointCraft contact data because a Customer in the account may
 * have been created outside Payforms.
 */
const ProviderCustomerNameSchema = z.string().trim().max(200).nullable();
const ProviderCustomerEmailSchema = z.string().trim().max(320).nullable();
const ProviderCustomerPhoneSchema = z.string().trim().max(40).nullable();
/**
 * Where a Customer came from, resolved from `pxCustomers` (O(1), no provider
 * call). `linked: false` means the Customer exists in the account but was not
 * created by a Payform signup.
 */
export const PayformCustomerSourceSchema = z.discriminatedUnion('linked', [
    z
        .object({
        linked: z.literal(true),
        sourceKind: z.literal(CheckoutSourceKindEnum.PUBLIC_EXPERIENCE_FORM),
        experienceId: ExperienceIdSchema,
        /** `null` when the experience was deleted or is not readable. */
        experienceTitle: ExperienceTitleSchema.nullable(),
        submissionId: SubmissionIdSchema,
        checkoutId: CheckoutIdSchema,
    })
        .strict(),
    z.object({ linked: z.literal(false) }).strict(),
]);
/** Customer list row (`customers.list` / `customers.search`). */
export const PayformCustomerSummarySchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    name: ProviderCustomerNameSchema,
    email: ProviderCustomerEmailSchema,
    phone: ProviderCustomerPhoneSchema,
    createdAt: IsoTimestampSchema,
    source: PayformCustomerSourceSchema,
})
    .strict();
/** A Payform submission linked to this Customer (via `provider.customerId`). */
export const PayformCustomerSubmissionLinkSchema = z
    .object({
    submissionId: SubmissionIdSchema,
    experienceId: ExperienceIdSchema,
    experienceTitle: ExperienceTitleSchema.nullable(),
    workflowStatus: SubmissionWorkflowStatusSchema,
    createdAt: IsoTimestampSchema,
})
    .strict();
/** Customer detail: profile + source association + linked submissions. */
export const PayformCustomerDetailSchema = PayformCustomerSummarySchema.extend({
    submissions: z.array(PayformCustomerSubmissionLinkSchema).max(100),
    /** When the provider was read, so a stale panel is recognizable. */
    readAt: IsoTimestampSchema,
}).strict();
// ---------------------------------------------------------------------------
// Payments (one PaymentIntent = one row; attempts are not rows)
// ---------------------------------------------------------------------------
/** Normalized PaymentIntent status. Anything else maps to FAILED/CANCELED by the backend, never to SUCCEEDED. */
export var PayformPaymentStatusEnum;
(function (PayformPaymentStatusEnum) {
    PayformPaymentStatusEnum["SUCCEEDED"] = "SUCCEEDED";
    PayformPaymentStatusEnum["PROCESSING"] = "PROCESSING";
    PayformPaymentStatusEnum["REQUIRES_ACTION"] = "REQUIRES_ACTION";
    PayformPaymentStatusEnum["FAILED"] = "FAILED";
    PayformPaymentStatusEnum["CANCELED"] = "CANCELED";
})(PayformPaymentStatusEnum || (PayformPaymentStatusEnum = {}));
export const PayformPaymentStatusSchema = z.nativeEnum(PayformPaymentStatusEnum);
export var PayformRefundStateEnum;
(function (PayformRefundStateEnum) {
    PayformRefundStateEnum["NONE"] = "NONE";
    PayformRefundStateEnum["PARTIAL"] = "PARTIAL";
    PayformRefundStateEnum["FULL"] = "FULL";
})(PayformRefundStateEnum || (PayformRefundStateEnum = {}));
export const PayformRefundStateSchema = z.nativeEnum(PayformRefundStateEnum);
/**
 * ISO 4217 code as reported by the provider, uppercased. Payform offers are
 * USD-only, but a connected account may carry other activity ("Other" rows),
 * and the list must not fail to parse because of it. Currencies are NEVER
 * summed across rows.
 */
export const PaymentCurrencyCodeSchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'must be an uppercase ISO 4217 code');
/** Card brand + last4 only. Never a full PAN, token or fingerprint. */
export const PayformPaymentMethodSummarySchema = z
    .object({
    brand: z.string().trim().min(1).max(40),
    last4: z.string().regex(/^\d{4}$/, 'must be four digits'),
})
    .strict();
/** Source label prefix for rows linked to a Payform submission. */
export const PAYFORM_PAYMENT_SOURCE_LABEL_PREFIX = 'Payform: ';
/** Source label for account activity not linked to a Payform. */
export const PAYFORM_PAYMENT_SOURCE_LABEL_OTHER = 'Other';
export const buildPayformSourceLabel = (experienceTitle) => `${PAYFORM_PAYMENT_SOURCE_LABEL_PREFIX}${experienceTitle}`;
/**
 * Payment-history row (PLAN §6.8). Identity is the PaymentIntent; an invoice is
 * NOT a payment and is listed separately (`PayformInvoiceRowSchema`). No
 * "paid out" wording, no lifetime totals.
 */
export const PayformPaymentRowSchema = z
    .object({
    paymentIntentId: StripePaymentIntentIdSchema,
    createdAt: IsoTimestampSchema,
    status: PayformPaymentStatusSchema,
    amountCents: MoneyCentsSchema,
    currency: PaymentCurrencyCodeSchema,
    refund: PayformRefundStateSchema,
    refundedCents: MoneyCentsSchema,
    /** The invoice this payment settled, when it was a subscription charge. */
    invoiceId: StripeInvoiceIdSchema.nullable(),
    /** `Payform: <title>` when linked via a submission, else `Other`. */
    sourceLabel: z.string().min(1).max(200),
    methodSummary: PayformPaymentMethodSummarySchema.nullable(),
})
    .strict()
    .superRefine((row, ctx) => {
    const consistent = (row.refund === PayformRefundStateEnum.NONE && row.refundedCents === 0) ||
        (row.refund === PayformRefundStateEnum.PARTIAL &&
            row.refundedCents > 0 &&
            row.refundedCents < row.amountCents) ||
        (row.refund === PayformRefundStateEnum.FULL &&
            row.amountCents > 0 &&
            row.refundedCents === row.amountCents);
    if (!consistent) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['refundedCents'],
            message: 'refundedCents must agree with the refund state',
        });
    }
});
// ---------------------------------------------------------------------------
// Recurring agreements and invoices (live reads; never stored)
// ---------------------------------------------------------------------------
/**
 * One subscription in the connected account, normalized. `manageable` is true
 * ONLY for a subscription PointCraft created in the business's current
 * execution scope (so it is linked to a submission); a cancel on any other
 * subscription is `PXE-019 SUBSCRIPTION_NOT_MANAGEABLE`.
 */
export const PayformRecurringAgreementSchema = z
    .object({
    billingPreview: BillingSchedulePreviewSchema.optional(),
    subscriptionId: StripeSubscriptionIdSchema,
    status: LiveRecurringStatusSchema,
    amountCents: PositiveMoneyCentsSchema,
    currency: PaymentCurrencyCodeSchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    manageable: z.boolean(),
    cancelAtPeriodEnd: z.boolean(),
    currentPeriodEnd: IsoTimestampSchema.nullable(),
    canceledAt: IsoTimestampSchema.nullable(),
    createdAt: IsoTimestampSchema,
    sourceLabel: z.string().min(1).max(200),
    /** The Payform submission this agreement belongs to, when PointCraft created it. */
    submissionId: SubmissionIdSchema.nullable(),
    readAt: IsoTimestampSchema,
})
    .strict()
    .superRefine((agreement, ctx) => {
    if (agreement.manageable && agreement.submissionId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['manageable'],
            message: 'a manageable agreement is always linked to a Payform submission',
        });
    }
});
/**
 * Invoice row — shown only on the recurring detail, separately from payments.
 * `paidOutOfBand`, credited and zero-amount invoices are not card payments and
 * never produce a payment row or a ledger entry.
 */
export const PayformInvoiceRowSchema = z
    .object({
    invoiceId: StripeInvoiceIdSchema,
    subscriptionId: StripeSubscriptionIdSchema.nullable(),
    createdAt: IsoTimestampSchema,
    status: LastInvoiceStatusSchema,
    amountDueCents: MoneyCentsSchema,
    amountPaidCents: MoneyCentsSchema,
    currency: PaymentCurrencyCodeSchema,
    /** The PaymentIntent that settled it, when one exists. */
    paymentIntentId: StripePaymentIntentIdSchema.nullable(),
    paidOutOfBand: z.boolean(),
})
    .strict();
